import { useFormContext } from 'react-hook-form'

import { useManagementGetAddress } from '@apps-common/apis/management/address/get-address'
import { StepDirection } from '@apps-common/apps/portal/pages/orders/pages/create/components/management'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm } from '@apps-common/components/elements/button/ButtonRoundedSm'
import { CollapsibleContent, VertSpacerLg } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { CreateOrderSteps } from '@apps-common/config/orders'
import { useManagementOrderSelectItems } from '@apps-common/hooks/useManagementOrderSelectItems'
import { formatPrice } from '@apps-common/utils/helpers'

interface IProps {
  isLoading: boolean
  isUpdating: boolean
  step: CreateOrderSteps
  move: (direction: StepDirection) => void
}

export const ManagementOrderSummaryForm: React.FC<IProps> = ({ isLoading, isUpdating, step, move }) => {
  const { getValues, formState } = useFormContext()
  const { isSubmitting, errors } = formState
  const { addressId, companyId, mCentreId, contactId, instructions, pickUp, orderLineItems, cost } = getValues() || {}
  const { isLoading: selectItemsIsLoading, companies, contacts } = useManagementOrderSelectItems()

  const {
    data: address,
    isLoading: addressIsloading,
    isError: addressIsError
  } = useManagementGetAddress({ config: { enabled: addressId?.length > 0 }, addressId })

  const company = companies?.find((c) => c.companyId === companyId)
  const monitoringCentre = companies?.find((c) => c.companyId === mCentreId)
  const contact = contacts?.find((c) => c.contactId === contactId)

  const WarningIcon = commonIcons.toastWarning

  const loading = isLoading || selectItemsIsLoading || addressIsloading || addressIsError
  const updating = isUpdating || isSubmitting

  return (
    <CollapsibleContent
      key='order-create-summary-form'
      title='Step 3 | Summary'
      defaultOpen={step === CreateOrderSteps.summary}
      disabled={step !== CreateOrderSteps.summary}
      classes='bg-surface border border-border rounded-2xl'
    >
      <VertSpacerLg />
      <div className='grid grid-cols-2 px-2 mb-[30px]'>
        <div>
          <SectionTitle text='Details' />
          <p>{company?.companyName}</p>
          <p>{monitoringCentre?.companyName}</p>
          <p>
            {contact?.name} {contact?.companyName && `(${contact.companyName})`}
          </p>
        </div>
        <div>
          <SectionTitle text='Delivery' />
          {pickUp && <p>Order to be collected</p>}
          {!pickUp && (
            <>
              <p>{address?.streetAddress1}</p>
              {address?.streetAddress2 && <p>{address.streetAddress2}</p>}
              {address?.streetAddress3 && <p>{address.streetAddress3}</p>}
              {address?.streetAddress4 && <p>{address.streetAddress4}</p>}
              {address?.city && <p>{address.city}</p>}
              {address?.postCode && <p>{address.postCode}</p>}
            </>
          )}
        </div>
      </div>
      <div className='overflow-hidden border border-border rounded-xl mt-[1rem] text-text'>
        <div className='jost-light text-[0.8em] sm:text-[0.9em]'>
          <div className='jost-medium text-[0.9em] bg-surface-2 border-b border-border py-[0.5rem] sm:py-[1rem] px-[1rem] rounded-t-xl'>
            <div className='flex items-center justify-start gap-[10px]'>
              <div>Order items</div>
            </div>
          </div>
          {orderLineItems?.length > 0 &&
            orderLineItems.map((field: any, index: number) => (
              <div key={`orderline-item-${index}`} className='flex items-center justify-between gap-[5px] p-6'>
                <p className='grow flex items-center justify-start gap-[5px]'>
                  {field.productQuantity} x {field.productName}
                </p>
                {field.discount > 0 && <p className='text-[0.75em] jost-medium'>{field.discount}% discount</p>}
                <p>{formatPrice(field.cost, 2)}</p>
              </div>
            ))}
          <div className='jost-medium text-[0.9em] bg-surface-2 border-t border-border py-[0.5rem] sm:py-[1rem] px-[1rem] rounded-b-xl'>
            <div className='flex items-center justify-end gap-[10px]'>
              <div>Total: {formatPrice(cost, 2)}</div>
            </div>
          </div>
        </div>
      </div>
      <p className='pl-2 pt-2 text-error text-xs'>{errors?.orderLineItems?.root?.message as any}</p>
      <VertSpacerLg />
      <div className='flex items-center justify-end gap-[10px]'>
        <ButtonRoundedSm
          type='button'
          loading={loading || updating}
          text='Back'
          callback={() => move('backward')}
          icon={<commonIcons.arrowAngledLeft />}
          className='grow sm:grow-0 action-neutral'
        />
        <ButtonRoundedSm
          type='submit'
          loading={loading || updating}
          text='Done'
          className='grow sm:grow-0 action-success'
          icon={<commonIcons.arrowAngled />}
          iconRight
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
